import React, { useContext, useEffect, useId, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { useLocation } from "react-router-dom";
import { Field, Form, useField, useFormState } from "react-final-form";
import moment from "moment";
import { AuthContext, IntlContext } from "context";
import { ChevronDown, FileText, Mail, MapPin, ShieldCheck, Sparkles, User } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "lib/utils";
import Address from "components/Form/Address";
import { Slider, TextField, Textarea } from "components/Form/Field";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "components/ui/select";
import FieldControl from "ui/Form/Element/FieldControl";
import FieldGroup from "ui/Form/Element/FieldGroup";
import Feedback from "ui/Form/Element/Feedback";
import Label from "ui/Form/Element/Label";
import { Button } from "components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "components/ui/dialog";
import { Input } from "components/ui/input";
import { ROUTES } from "routes";
import { Role } from "types/user";
import { useReferences } from "hooks/reference";
import ClientCesuProfileSection from "./ClientCesuProfileSection";
import {
    buildAddressUpdatePayload,
    buildProviderMyProfilePayload,
} from "helpers/profile/buildProfilePayload";
import { deleteMyAccount, updateMyProfile, updateProfileCesu } from "hooks/profile";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { getServerMessage } from "lib/serverError";
import { toast } from "sonner";

interface Props {
    onSaved?: () => void;
}

function toFiniteNumber(value: unknown): number | null {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const n = Number(value);
        return Number.isFinite(n) ? n : null;
    }
    return null;
}

interface MyProfileFormValues {
    description?: string;
    travel_range?: number;
    address?: string;
    coordinates_lat?: number | string;
    coordinates_lon?: number | string;
    house_number?: string;
    street?: string;
    postcode?: string;
    city?: string;
    commune_code?: string;
    resources?: {
        provider?: {
            has_cesu?: string;
            num_cesu?: string;
        };
    };
}

const getCesuHasOptions = (intl: { formatMessage: (id: string) => string }) =>
    [
        { value: "1", label: intl.formatMessage("generic.label.yes") },
        { value: "0", label: intl.formatMessage("generic.label.no") },
    ] as const;

const PROFILE_TRAVEL_RANGE_HASH = "#travel-range";

const PROVIDER_DESCRIPTION_MIN_LENGTH = 50;
const PROVIDER_DESCRIPTION_MAX_LENGTH = 2000;

const profileFieldLabelClassName = "text-xs font-semibold uppercase tracking-wide text-slate-500";
const providerDescriptionTextareaClassName =
    "w-full min-h-[170px] rounded-[28px] border border-input bg-muted/50 px-5 py-4 text-sm leading-relaxed placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const profileSelectTriggerClassName =
    "h-11 w-full cursor-pointer justify-between gap-2 rounded-xl border-0 bg-slate-50 px-3 text-sm text-gray-900 shadow-none focus:ring-2 focus:ring-ring focus:ring-offset-0 [&>svg]:text-slate-400";

const ProfileSection: React.FC<{
    icon: React.ElementType;
    title: string;
    defaultOpen?: boolean;
    children: React.ReactNode;
}> = ({ icon: Icon, title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    const contentId = useId();

    return (
        <section>
            <h3 className="border-b border-gray-100">
                <button
                    type="button"
                    aria-expanded={open}
                    aria-controls={contentId}
                    onClick={() => setOpen((current) => !current)}
                    className="flex w-full items-center justify-between gap-3 pb-3 text-left text-gray-900 transition-colors hover:text-primary"
                >
                    <span className="flex items-center gap-2">
                        <Icon className="h-5 w-5 text-primary" strokeWidth={2.25} />
                        <span className="text-base font-bold">{title}</span>
                    </span>
                    <ChevronDown
                        className={cn(
                            "h-5 w-5 shrink-0 text-slate-400 transition-transform duration-200",
                            open && "rotate-180",
                        )}
                        aria-hidden
                    />
                </button>
            </h3>
            <div
                className={cn("grid transition-[grid-template-rows] duration-200 ease-out", open ? "grid-rows-[1fr]" : "grid-rows-[0fr]")}
            >
                <div
                    id={contentId}
                    className="min-h-0 overflow-hidden"
                    onInvalidCapture={() => {
                        if (!open) {
                            flushSync(() => setOpen(true));
                        }
                    }}
                >
                    <div className={cn("pt-4", !open && "invisible pointer-events-none")}>{children}</div>
                </div>
            </div>
        </section>
    );
};

const ReadOnlyField: React.FC<{ label: string; value?: string; icon?: React.ReactNode }> = ({
    label,
    value,
    icon,
}) => (
    <div className="space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        <div className="relative">
            {icon && <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">{icon}</span>}
            <Input
                readOnly
                value={value ?? ""}
                className={`h-11 rounded-xl border-0 bg-slate-50 text-gray-900 ${icon ? "pl-10" : ""}`}
            />
        </div>
    </div>
);

const ProviderDescriptionField: React.FC = () => {
    const { values } = useFormState<MyProfileFormValues>({ subscription: { values: true } });
    const descriptionLength = String(values?.description ?? "").length;
    const hasMinDescriptionLength = descriptionLength >= PROVIDER_DESCRIPTION_MIN_LENGTH;

    return (
        <ProfileSection icon={FileText} title="Ma présentation">
            <div className="space-y-4">
                <Textarea
                    inputProps={{
                        name: "description",
                        required: true,
                        minLength: PROVIDER_DESCRIPTION_MIN_LENGTH,
                        maxLength: PROVIDER_DESCRIPTION_MAX_LENGTH,
                        placeholder:
                            "Présentez-vous à vos voisins : votre expérience, vos motivations, ce que vous aimez dans les services que vous proposez...",
                        rows: 5,
                        className: providerDescriptionTextareaClassName,
                    }}
                    labelProps={{
                        label: "À propos de vous",
                        className: profileFieldLabelClassName,
                    }}
                    fieldControlProps={{
                        className:
                            "[&_textarea]:min-h-[140px] [&_textarea]:rounded-xl [&_textarea]:border-0 [&_textarea]:bg-slate-50",
                    }}
                />
                <div className="flex flex-wrap items-center justify-between gap-4 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <Sparkles className="h-3 w-3" /> Inspirez confiance !
                    </div>
                    <div className="flex flex-wrap items-center gap-4">
                        <div>
                            <span className="text-primary">{descriptionLength}</span>/{PROVIDER_DESCRIPTION_MAX_LENGTH}{" "}
                            caractères
                        </div>
                        <div>
                            Min.{" "}
                            <span className={hasMinDescriptionLength ? "text-emerald-600" : "text-red-600"}>
                                {PROVIDER_DESCRIPTION_MIN_LENGTH}
                            </span>{" "}
                            caractères
                        </div>
                    </div>
                </div>
            </div>
        </ProfileSection>
    );
};

function hasValidAddress(values: Pick<
    MyProfileFormValues,
    "address" | "coordinates_lat" | "coordinates_lon" | "postcode" | "city"
>): boolean {
    const address = String(values.address ?? "").trim();
    const lat = toFiniteNumber(values.coordinates_lat);
    const lon = toFiniteNumber(values.coordinates_lon);
    const postcode = String(values.postcode ?? "").trim();
    const city = String(values.city ?? "").trim();

    return address.length > 0 && lat != null && lon != null && postcode.length > 0 && city.length > 0;
}

const ProviderTravelRangeField: React.FC = () => {
    const intl = useContext(IntlContext);
    const { hash } = useLocation();
    const { values } = useFormState<MyProfileFormValues>({ subscription: { values: true } });
    const addressField = useField("address", { subscription: { value: true } });
    const latitudeField = useField("coordinates_lat", { subscription: { value: true } });
    const longitudeField = useField("coordinates_lon", { subscription: { value: true } });
    const postcodeField = useField("postcode", { subscription: { value: true } });
    const cityField = useField("city", { subscription: { value: true } });

    const travelRange = toFiniteNumber(values?.travel_range) ?? 10;
    const hasAddress = hasValidAddress({
        address: addressField.input.value,
        coordinates_lat: latitudeField.input.value,
        coordinates_lon: longitudeField.input.value,
        postcode: postcodeField.input.value,
        city: cityField.input.value,
    });

    useEffect(() => {
        if (hash !== PROFILE_TRAVEL_RANGE_HASH) return;

        const timer = window.setTimeout(() => {
            document.getElementById("travel-range")?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);

        return () => window.clearTimeout(timer);
    }, [hash]);

    return (
        <div
            id="travel-range"
            className={`scroll-mt-24 space-y-5 ${!hasAddress ? "pointer-events-none opacity-50" : ""}`}
            aria-disabled={!hasAddress}
        >
            <div className="flex items-center justify-between">
                <span className={profileFieldLabelClassName}>
                    {intl.formatMessage("profile.label.travel_range")} *
                </span>
                <span className="text-xl font-bold text-foreground">{travelRange} km</span>
            </div>

            <Slider
                parse={(value) => {
                    const n = toFiniteNumber(value);
                    return typeof n === "number" ? n : value;
                }}
                inputProps={{
                    name: "travel_range",
                    min: 5,
                    max: 50,
                    step: 1,
                    unit: "km",
                    required: hasAddress,
                    disabled: !hasAddress,
                }}
            />
        </div>
    );
};

const CesuNumberField: React.FC = () => {
    const { values } = useFormState<MyProfileFormValues>({ subscription: { values: true } });

    if (values?.resources?.provider?.has_cesu !== "1") {
        return null;
    }

    return (
        <div>
            <TextField
                inputProps={{
                    name: "resources.provider.num_cesu",
                    placeholder: "XXX XXX XXX XXX",
                    maxLength: 14,
                    required: true,
                }}
                labelProps={{
                    label: "N° CESU",
                    className: "text-xs font-semibold uppercase tracking-wide text-slate-500",
                }}
                fieldControlProps={{
                    className: "[&_input]:h-11 [&_input]:rounded-xl [&_input]:border-0 [&_input]:bg-slate-50",
                }}
            />
            <p className="mt-2 text-xs text-muted-foreground">
                Vous pouvez retrouver votre n° CESU soit dans votre espace personnel sur le site du CESU, soit sur la
                dernière fiche de paie éditée par l'URSSAF.
            </p>
        </div>
    );
};

const MyProfileForm: React.FC<Props> = ({ onSaved }) => {
    const intl = useContext(IntlContext);
    const { account, currentRole, logout } = useContext(AuthContext);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const isProvider = currentRole === Role.PROVIDER;
    const isClient = currentRole === Role.CLIENT;
    const provider = account?.resources?.provider?.[0];
    const client = account?.resources?.client?.[0];
    const reference = useReferences();
    const countries = reference.all("countries");

    const countryBirthLabel = useMemo(() => {
        const match = countries.find((c) => c.code_iso === account?.user.country_birth_code);
        return match?.value ?? account?.user.country_birth_code ?? "";
    }, [account?.user.country_birth_code, countries]);

    const initialValues = useMemo<MyProfileFormValues>(() => {
        const lat = account?.user.coordinates?.lat;
        const lon = account?.user.coordinates?.lon;

        const base: MyProfileFormValues = {
            address: account?.user.address ?? "",
            coordinates_lat: lat !== undefined ? String(lat) : undefined,
            coordinates_lon: lon !== undefined ? String(lon) : undefined,
            house_number: account?.user.house_number,
            street: account?.user.street,
            postcode: account?.user.postcode,
            city: account?.user.city,
            commune_code: account?.user.commune_code,
        };

        if (isProvider) {
            return {
                ...base,
                description: provider?.description ?? "",
                travel_range: provider?.travel_range ?? 10,
                resources: {
                    provider: {
                        has_cesu: provider?.has_cesu ?? "0",
                        num_cesu: provider?.num_cesu ?? "",
                    },
                },
            };
        }

        return base;
    }, [account, isProvider, provider]);

    const onSubmit = async (values: MyProfileFormValues) => {
        if (!account) return;

        try {
            if (isProvider && provider) {
                const addressChanged = values.address !== account.user.address;
                const travelRangeChanged = values.travel_range !== provider.travel_range;
                const cesuChanged =
                    values.resources?.provider?.has_cesu !== provider.has_cesu ||
                    values.resources?.provider?.num_cesu !== provider.num_cesu;

                await updateMyProfile(
                    account.user.id,
                    buildProviderMyProfilePayload(values, {
                        includeAddress: addressChanged,
                        includeTravelRange: travelRangeChanged,
                    }),
                );

                if (cesuChanged) {
                    await updateProfileCesu(account.user.id, {
                        resources: {
                            provider: {
                                has_cesu: values.resources?.provider?.has_cesu,
                                num_cesu: values.resources?.provider?.num_cesu,
                            },
                        },
                    });
                }
            } else if (isClient && client) {
                const addressChanged = values.address !== account.user.address;
                if (addressChanged) {
                    await updateMyProfile(
                        account.user.id,
                        buildAddressUpdatePayload(values),
                    );
                }
            }

            toast.success(intl.formatMessage("profile.user.data.update"));
            onSaved?.();
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            toast.error(intl.formatMessage("profile.user.data.update.error"));
            throw err;
        }
    };

    const handleDeleteAccount = async () => {
        try {
            setIsDeletingAccount(true);
            await deleteMyAccount();
            setShowDeleteModal(false);
            logout();
        } catch (err: unknown) {
            toast.error(
                getServerMessage(err) ?? intl.formatMessage("profile.account.action.delete.error"),
            );
        } finally {
            setIsDeletingAccount(false);
        }
    };

    if (!account || (isProvider && !provider) || (isClient && !client)) {
        return null;
    }

    return (
        <div className="rounded-2xl bg-white p-6 shadow-sm sm:p-8">
            <Form
                onSubmit={onSubmit}
                initialValues={initialValues}
                render={({ handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit} className="space-y-10">
                        <ProfileSection icon={User} title="Informations de base">
                            <div className="space-y-4">
                                <p className="text-sm text-slate-500">
                                    Ces informations ne sont pas modifiables. Si vous devez les modifier, veuillez{" "}
                                    <Link to={ROUTES.CONTACT} className="font-medium text-primary hover:underline">
                                        contacter nos équipes
                                    </Link>
                                    .
                                </p>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-1">
                                    {isClient && (
                                        <ReadOnlyField
                                            label={intl.formatMessage("profile.label.gender")}
                                            value={
                                                account.user.gender
                                                    ? intl.formatMessage(
                                                        `generic.label.gender.${account.user.gender}`,
                                                    )
                                                    : ""
                                            }
                                        />
                                    )}
                                </div>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <ReadOnlyField label="Prénom" value={account.user.first_name} />
                                    <ReadOnlyField label="Nom" value={account.user.last_name} />
                                    <ReadOnlyField
                                        label="Email"
                                        value={account.user.email}
                                        icon={<Mail className="h-4 w-4 text-slate-400" />}
                                    />
                                    <ReadOnlyField label="Téléphone" value={account.user.phone} />
                                    {isClient && client && (
                                        <ReadOnlyField
                                            label={intl.formatMessage("profile.label.birthday")}
                                            value={moment(client.birthday).format("DD/MM/YYYY")}
                                        />
                                    )}
                                    {isProvider && provider && (
                                        <>
                                            <ReadOnlyField
                                                label={intl.formatMessage("profile.label.nir")}
                                                value={provider.nir}
                                            />
                                            <ReadOnlyField
                                                label={intl.formatMessage("authentication.register.label.birthday")}
                                                value={moment(provider.birthday).format("DD/MM/YYYY")}
                                            />
                                            <ReadOnlyField
                                                label={intl.formatMessage("register.label.country.birth")}
                                                value={countryBirthLabel}
                                            />
                                            <ReadOnlyField
                                                label={intl.formatMessage("register.label.department.birth")}
                                                value={account.user.department_birth_code ?? ""}
                                            />
                                            <ReadOnlyField
                                                label={intl.formatMessage("register.label.city.birth")}
                                                value={account.user.city_birth ?? ""}
                                            />
                                        </>
                                    )}
                                </div>
                            </div>
                        </ProfileSection>

                        {isProvider && <ProviderDescriptionField />}
                        <ProfileSection icon={MapPin} title="Localisation" defaultOpen>
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
                                <Address
                                    inputProps={{
                                        name: "address",
                                        latitudeName: "coordinates_lat",
                                        longitudeName: "coordinates_lon",
                                        houseNumber: "house_number",
                                        street: "street",
                                        postcode: "postcode",
                                        city: "city",
                                        commune_code: "commune_code",
                                        required: true,
                                    }}
                                    labelProps={{
                                        label: intl.formatMessage("profile.label.address"),
                                        className: profileFieldLabelClassName,
                                    }}
                                    fieldControlProps={{
                                        className:
                                            "relative [&_.field-control__autocomplete]:block [&_input]:h-11 [&_input]:rounded-xl [&_input]:border-0 [&_input]:bg-slate-50",
                                    }}
                                />
                                {isProvider && <ProviderTravelRangeField />}
                            </div>
                        </ProfileSection>

                        {isClient && (
                            <ProfileSection icon={ShieldCheck} title="CESU employeur">
                                <ClientCesuProfileSection />
                            </ProfileSection>
                        )}

                        {isProvider && (
                            <ProfileSection icon={ShieldCheck} title="CESU">
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:items-start">
                                <Field name="resources.provider.has_cesu">
                                    {({ input, meta }) => {
                                        const error = Boolean(meta.error || meta.submitError);
                                        const raw = input.value;
                                        const selectValue =
                                            raw === undefined || raw === null || String(raw).trim() === ""
                                                ? undefined
                                                : String(raw);

                                        return (
                                            <FieldGroup error={error}>
                                                <Label
                                                    htmlFor="resources.provider.has_cesu"
                                                    className={profileFieldLabelClassName}
                                                    required
                                                >
                                                    Avez-vous un numéro CESU salarié ?
                                                </Label>
                                                <FieldControl error={error}>
                                                    <Select
                                                        value={selectValue}
                                                        onValueChange={input.onChange}
                                                        onOpenChange={(open) => {
                                                            if (!open) {
                                                                input.onBlur();
                                                            }
                                                        }}
                                                        required
                                                    >
                                                        <SelectTrigger
                                                            id="resources.provider.has_cesu"
                                                            aria-invalid={error}
                                                            className={profileSelectTriggerClassName}
                                                        >
                                                            <SelectValue placeholder="Sélectionner" />
                                                        </SelectTrigger>
                                                        <SelectContent
                                                            sideOffset={8}
                                                            className="max-h-72 rounded-2xl border border-input bg-popover py-1.5 text-sm text-popover-foreground shadow-lg ring-1 ring-border/40"
                                                        >
                                                            {getCesuHasOptions(intl).map((option) => (
                                                                <SelectItem
                                                                    key={option.value}
                                                                    value={option.value}
                                                                    className="rounded-xl py-2 pl-8 pr-3"
                                                                >
                                                                    {option.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </FieldControl>
                                                {error && (
                                                    <Feedback className="field__feedback error">
                                                        {meta.error || meta.submitError}
                                                    </Feedback>
                                                )}
                                            </FieldGroup>
                                        );
                                    }}
                                </Field>
                                <CesuNumberField />
                                </div>
                            </ProfileSection>
                        )}

                        <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:items-center sm:justify-between">
                            <Button
                                type="button"
                                variant="outline"
                                size="lg"
                                className="rounded-2xl text-destructive border-destructive hover:bg-destructive/10 hover:text-destructive"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                {intl.formatMessage("profile.account.action.delete")}
                            </Button>
                            <Button type="submit" size="lg" disabled={submitting}>
                                Enregistrer les modifications
                            </Button>
                        </div>
                    </form>
                )}
            />

            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{intl.formatMessage("profile.account.action.delete.modal.title")}</DialogTitle>
                        <DialogDescription>
                            {intl.formatMessage("profile.account.action.delete.modal.message")}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2 sm:gap-0">
                        <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                            {intl.formatMessage("generic.action.cancel")}
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={isDeletingAccount}
                            onClick={() => void handleDeleteAccount()}
                        >
                            {intl.formatMessage("generic.label.delete")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MyProfileForm;
